import React, { useEffect, useRef } from 'react';
import { Megaphone } from 'lucide-react';

/**
 * Google AdSense Components
 * Publisher ID: ca-pub-6943486061779023
 */

const TEST_MODE = false;
const ADSENSE_CLIENT = 'ca-pub-6943486061779023';
const AD_SLOTS = {
  banner: '5821382201',
  sidebar: 'XXXXXXXXXX',
  inFeed: '3879606960',
};

// Banner Ad Component - For between sections
export const AdBanner = ({ 
  slot = 'banner', 
  format = 'horizontal',
  className = '' 
}) => {
  const adRef = useRef(null);
  const isAdLoaded = useRef(false);

  useEffect(() => {
    if (TEST_MODE || isAdLoaded.current) return;
    
    try {
      if (adRef.current && window.adsbygoogle) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        isAdLoaded.current = true;
      }
    } catch (e) {
      console.error('AdSense error:', e);
    }
  }, []);

  if (TEST_MODE) {
    return (
      <div className={`flex justify-center ${className}`}>
        <div 
          className="relative bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 border border-dashed border-zinc-700 rounded-lg flex flex-col items-center justify-center overflow-hidden"
          style={{ width: '100%', maxWidth: '728px', height: '90px' }}
        >
          <div className="text-center px-4">
            <Megaphone className="w-6 h-6 text-zinc-600 mx-auto mb-2" />
            <p className="text-xs text-zinc-500 font-medium uppercase tracking-wider">Advertisement</p>
          </div>
          <span className="absolute top-2 right-2 text-[9px] text-zinc-600 bg-zinc-800/50 px-1.5 py-0.5 rounded">
            Sponsored
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex justify-center ${className}`}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block', width: '100%', maxWidth: '728px', height: '90px' }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={AD_SLOTS[slot]}
        data-ad-format="horizontal"
        data-full-width-responsive="true"
      />
    </div>
  );
};

// Native Ad Card Component - For gallery/grid layouts
export const NativeAdCard = ({ className = '' }) => {
  const adRef = useRef(null);
  const isAdLoaded = useRef(false);

  useEffect(() => {
    if (TEST_MODE || isAdLoaded.current) return;
    
    try {
      if (adRef.current && window.adsbygoogle) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        isAdLoaded.current = true;
      }
    } catch (e) {
      console.error('AdSense error:', e);
    }
  }, []);

  if (TEST_MODE) {
    return (
      <div className={`group bg-zinc-900/50 border border-dashed border-zinc-700 rounded-lg overflow-hidden ${className}`}>
        <div className="relative aspect-video bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
          <div className="text-center">
            <Megaphone className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
            <p className="text-xs text-zinc-600">Sponsored Content</p>
          </div>
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded text-[10px] font-medium bg-zinc-800 text-zinc-500">
            Ad
          </span>
        </div>
        <div className="p-4">
          <div className="h-4 bg-zinc-800 rounded w-3/4 mb-2" />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-zinc-800" />
            <div className="h-3 bg-zinc-800 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-zinc-900/30 rounded-lg overflow-hidden border border-zinc-800 ${className}`}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={AD_SLOTS.inFeed}
        data-ad-format="fluid"
        data-ad-layout-key="-6t+ed+2i-1n-4w"
      />
    </div>
  );
};

// Sidebar Ad Component - For desktop sidebars
export const SidebarAd = ({ className = '' }) => {
  const adRef = useRef(null);
  const isAdLoaded = useRef(false);

  useEffect(() => {
    if (TEST_MODE || isAdLoaded.current) return;
    
    try {
      if (adRef.current && window.adsbygoogle) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        isAdLoaded.current = true;
      }
    } catch (e) {
      console.error('AdSense error:', e);
    }
  }, []);

  if (TEST_MODE) {
    return (
      <div className={`hidden xl:block ${className}`}>
        <div 
          className="sticky top-24 bg-gradient-to-b from-zinc-900 to-zinc-900/50 border border-dashed border-zinc-700 rounded-lg p-4 flex flex-col items-center justify-center"
          style={{ width: '160px', minHeight: '600px' }}
        >
          <Megaphone className="w-8 h-8 text-zinc-700 mb-3" />
          <p className="text-xs text-zinc-500 text-center uppercase tracking-wider">Sidebar Ad</p>
          <span className="text-[9px] text-zinc-600 bg-zinc-800/50 px-2 py-1 rounded mt-4">Sponsored</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`hidden xl:block ${className}`}>
      <div className="sticky top-24">
        <ins
          ref={adRef}
          className="adsbygoogle"
          style={{ display: 'block', width: '160px', height: '600px' }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={AD_SLOTS.sidebar}
        />
      </div>
    </div>
  );
};

export default { AdBanner, NativeAdCard, SidebarAd };
